import { Box } from "@mui/material";
import {
  PercentileMode,
  ordinalSuffix,
  percentileColor,
} from "./percentileScale";

const CHIP_WIDTH = 26;

interface PercentileCellProps {
  /** Already-formatted display value — this component never formats. */
  value: React.ReactNode;
  /** 0-100. Null renders the value with no chip at all. */
  percentile: number | null | undefined;
  /** When set, the chip shows the rank instead of the percentile. Colour still
   *  comes from the percentile, so a 1st out of 8 doesn't read like a 1st of 400. */
  rank?: number;
  mode?: PercentileMode;
}

/**
 * A value with its percentile chip. Shared by the Explorer data grid and the
 * Player Stats grid so both read identically — see `percentileScale.ts` for why
 * the colours are computed rather than pulled from a stylesheet.
 */
export default function PercentileCell({
  value,
  percentile,
  rank,
  mode = "percentiles",
}: PercentileCellProps) {
  const showChip = percentile != null && !Number.isNaN(percentile);
  const color = percentileColor(percentile, mode);

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      gap={0.6}
      width="100%"
      sx={{ fontVariantNumeric: "tabular-nums" }}
    >
      <Box component="span" flexGrow={1} textAlign="right" overflow="hidden">
        {value}
      </Box>
      {showChip && (
        <Box
          component="span"
          sx={{
            // Fixed width, not min-width, and the same width in both modes:
            // chips have to be one size or the ramp reads as noise, and a column
            // must not resize when the grid is toggled between percentiles and
            // ranks.
            flex: "0 0 auto",
            width: CHIP_WIDTH,
            px: "2px",
            py: "1px",
            borderRadius: "2px",
            backgroundColor: color.fill,
            color: color.text,
            // `text-shadow` applies to the glyphs only, so unlike a filter it can
            // sit on the chip itself without tracing its rounded rectangle.
            textShadow: color.textOutline,
            fontSize: 10.5,
            fontWeight: 700,
            lineHeight: 1.3,
            textAlign: "center",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {rank ? (
            <>
              {rank}
              <sup style={{ verticalAlign: "unset" }}>{ordinalSuffix(rank)}</sup>
            </>
          ) : (
            Math.round(percentile as number)
          )}
        </Box>
      )}
    </Box>
  );
}
